import * as React from 'react';

import * as ramda from 'ramda';

import {serializeFilters} from 'nutshell-core';

import {api} from 'nutshell-core/api';
import {Routing} from 'nutshell-core/routing';
import type {CoreEntityType, FilterObject} from 'nutshell-core/types';

import {Link} from 'shells/link/link';
import {ListViewInfiniteLoading as ListLoadingIcon} from 'shells/list-view/list-view-infinite-loading';
import {Modal} from 'shells/modal/modal';
import {SelectableListSingle} from 'shells/selectable-list/selectable-list';
import {Title} from 'shells/typography/title';

// @ts-expect-error
import getClasses from '../../../../mixins/get-classes';

import {BulkEmailSendButtons} from '../bulk-email-send-buttons';
import {BulkEmailStatus} from '../bulk-email-status';

import styles from './bulk-personal-email-modal.module.css';

const MAX_API_IDS_TO_SEND_IN_URL = 200;

type TemplatePreview = {
    mungedSubject: string;
    mungedBody: string;
    missingFieldsHref: string;
    missingEmailsHref: string;
    noMissingHref: string;
    missingFieldsCount: number;
    missingEmailsCount: number;
    hasReceivedTemplateRecentlyCount: number;
    missingFieldsAndReceivedRecentlyCount: number;
    cannotSendCount: number;
    totalCount: number;
};

export type SequenceTemplate = {
    id: string;
    name: string;
    numEmails?: number;
    isDisabled?: boolean;
    hasCustomPlaceholders?: boolean;
    tooltipText?: string;
    type: 'emailSequenceTemplates';
};

export type SelectionQuery = {
    filter: {
        selected: FilterObject[];
    };
    search: {
        selected: string;
    };
};

export type Props = {
    userId: string;
    entityType: CoreEntityType;
    query: SelectionQuery;
    massSelect: boolean;
    selectedItems: string[];
    omittedItems: string[];
    onClose: () => any;
    onEmailSend: (
        selectedTemplate: SequenceTemplate,
        arg2: {
            includeIncomplete: boolean;
            includeRecentlyReceivedTemplate: boolean;
        }
    ) => any;
};

type State = {
    selectedTemplate: SequenceTemplate | null | undefined;
    preview: TemplatePreview | null | undefined;
    error: any | null | undefined;
    templates: SequenceTemplate[];
    isLoadingTemplates: boolean;
    isLoadingPreview: boolean;
};

export class BulkPersonalEmailModal extends React.Component<Props, State> {
    getClasses = getClasses;
    emailTemplateUrl = '/email/templates';

    constructor(props: Props) {
        super(props);
        this.state = {
            preview: null,
            selectedTemplate: null,
            error: null,
            templates: [],
            isLoadingTemplates: true,
            isLoadingPreview: false,
        };
    }

    componentDidMount() {
        api.get('emailsequencetemplates', {userId: this.props.userId})
            // @ts-expect-error
            .then((res) => res.json())
            .then((json) => {
                // @ts-expect-error
                const templates = json.emailSequenceTemplates.map((template) => {
                    const isMultiSequenceMessage = template.numEmails > 1;

                    return {
                        id: template.id,
                        name: template.name,
                        type: template.type,
                        isDisabled: isMultiSequenceMessage || template.hasCustomPlaceholders,
                        hasCustomPlaceholders: template.hasCustomPlaceholders || false,
                        tooltipText: getToolTipMessage(
                            isMultiSequenceMessage,
                            template.hasCustomPlaceholders
                        ),
                    };
                });

                this.setState({
                    isLoadingTemplates: false,
                    templates: ramda.sortBy(ramda.prop('isDisabled'))(templates),
                });
            });
    }

    render() {
        const {onClose} = this.props;
        const emailStats = this.state.preview
            ? {
                  cannotSendCount: this.state.preview.cannotSendCount,
                  totalCount: this.state.preview.totalCount,
                  missingFieldsCount: this.state.preview.missingFieldsCount,
                  hasReceivedTemplateRecentlyCount:
                      this.state.preview.hasReceivedTemplateRecentlyCount,
                  missingFieldsAndReceivedRecentlyCount:
                      this.state.preview.missingFieldsAndReceivedRecentlyCount,
              }
            : null;

        return (
            <Modal isOpen={true} headerText='Send email' onClose={onClose}>
                <div className='ui-bulk-email-modal'>
                    <React.Fragment>
                        {!this.state.isLoadingPreview ? (
                            <BulkEmailStatus
                                error={this.state.error}
                                recipientStats={this.state.preview}
                            />
                        ) : undefined}
                        <div className={styles.title}>
                            <Title>
                                <span>Email templates&nbsp;</span>
                                <Link
                                    newTab={true}
                                    size='small'
                                    variant='primary'
                                    href={this.emailTemplateUrl}
                                >
                                    Manage templates…
                                </Link>
                            </Title>
                        </div>
                        <div className={styles['main-content']}>
                            {/* @ts-expect-error */}
                            <SelectableListSingle
                                collection={this.state.templates}
                                onSelect={this.handleTemplateSelection}
                                value={
                                    this.state.selectedTemplate
                                        ? this.state.selectedTemplate.id
                                        : null
                                }
                                emptyStateComponent={
                                    <div className='ui-column ui-column-headers'>
                                        {this.renderEmptyState()}
                                    </div>
                                }
                            />
                            <div className='ui-bulk-email-preview'>
                                {this.state.selectedTemplate
                                    ? this.renderPreview(
                                          this.state.selectedTemplate,
                                          this.state.preview
                                      )
                                    : this.renderSelectTemplatePlaceholder()}
                            </div>
                        </div>
                        <BulkEmailSendButtons
                            send={this.handleSend}
                            emailStats={emailStats}
                            hasError={Boolean(this.state.error)}
                        />
                    </React.Fragment>
                </div>
            </Modal>
        );
    }

    renderEmptyState = () => {
        const {templates, isLoadingTemplates} = this.state;

        if (templates.length) {
            return <p>We couldn’t find any templates with that name.</p>;
        }

        if (isLoadingTemplates) {
            return <ListLoadingIcon />;
        }

        if (!templates.length) {
            return (
                <div>
                    <p>
                        You can bulk email templates with a single email, but it looks like you
                        don’t have any yet.
                    </p>
                    <p>
                        <a href={this.emailTemplateUrl}>Create a new template</a>
                    </p>
                </div>
            );
        }
    };

    renderSelectTemplatePlaceholder() {
        return (
            <div className='ui-bulk-email-modal__empty-selection'>
                <div className='ui-bulk-email-modal__empty-selection-title'>
                    Select a template to preview email
                </div>
                <div className='ui-bulk-email-modal__empty-selection-subject-line' />
                <div className='ui-bulk-email-modal__empty-selection-body'>
                    <div className='ui-bulk-email-modal__empty-selection-body-line' />
                    <div className='ui-bulk-email-modal__empty-selection-body-line' />
                    <div className='ui-bulk-email-modal__empty-selection-body-line' />
                </div>
                <div className='ui-bulk-email-modal__empty-selection-body'>
                    <div className='ui-bulk-email-modal__empty-selection-body-line' />
                    <div className='ui-bulk-email-modal__empty-selection-body-line' />
                    <div className='ui-bulk-email-modal__empty-selection-body-line' />
                </div>
                <div className='ui-bulk-email-modal__empty-selection-subject-line' />
            </div>
        );
    }

    renderPreview(selectedTemplate: SequenceTemplate, preview?: TemplatePreview | null) {
        const {isLoadingPreview} = this.state;

        const mungedSubject = preview ? preview.mungedSubject : '';
        const mungedBody = preview ? preview.mungedBody : '';

        const subject = mungedSubject ? (
            <span>{mungedSubject}</span>
        ) : (
            <span>
                <i>No subject</i>
            </span>
        );
        const body = Nut.View.nl2br(mungedBody);

        // When using dangerouslySetInnerHTML, we have use a span, or
        // the first child disappears on the initial render.
        // See: https://github.com/facebook/react/issues/1232
        return (
            <div className='ui-bulk-email-selected-preview'>
                <div className='ui-bulk-email-selected-preview__preview'>
                    Previewing: {selectedTemplate.name}
                </div>
                {isLoadingPreview ? (
                    <ListLoadingIcon />
                ) : (
                    <div>
                        <div className='ui-bulk-email-selected-preview__subject'>{subject}</div>
                        <div className='ui-bulk-email-selected-preview__body'>
                            {}
                            <span dangerouslySetInnerHTML={{__html: body}} />
                            {}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    handleSend = (options: {
        includeIncomplete: boolean;
        includeRecentlyReceivedTemplate: boolean;
    }) => {
        if (this.state.selectedTemplate) {
            this.props.onEmailSend(this.state.selectedTemplate, options);
        }

        this.props.onClose();
    };

    handleTemplateSelection = (selectedTemplateId?: string | null) => {
        // It's okay to break this rule, because the templates state doesn't really change

        const emailTemplate = this.state.templates.find((model) => {
            return model.id === selectedTemplateId;
        });

        if (!emailTemplate) return;

        this.setState({
            selectedTemplate: emailTemplate,
            isLoadingPreview: true,
        });

        const params: any = {entityType: this.props.entityType};
        if (this.props.massSelect) {
            if (this.props.query.filter) {
                params.filter = serializeFilters(this.props.query.filter.selected);
            }
            if (this.props.query.search && this.props.query.search.selected) {
                params.q = this.props.query.search.selected;
            }
            if (this.props.omittedItems.length > 0) {
                params.omittedItems = this.props.omittedItems.join(',');
            }
        } else if (this.props.selectedItems) {
            // NUT-29575 - A user was receiving a 414 HTTP error code due to the URL exceeding the length limit.
            // If a user exceeds our id limit, we'll convert to integer ids to save space.
            if (this.props.selectedItems.length > MAX_API_IDS_TO_SEND_IN_URL) {
                const integerIds = convertApiIdsToIntegerIds(this.props.selectedItems);
                params.integerIds = integerIds.join(',');
            } else {
                params.ids = this.props.selectedItems.join(',');
            }
        }

        $.ajax({
            type: 'GET',
            url: decodeURI(
                `/rest/emailsequencetemplates/${emailTemplate.id}/preview?${Routing.param(params)}`
            ),
        })
            .then((response) => {
                const {
                    emailTemplates: [preview],
                    missingEmailsCount,
                    missingFieldsCount,
                    hasReceivedTemplateRecentlyCount,
                    missingFieldsAndReceivedRecentlyCount,
                    cannotSendCount,
                    totalCount,
                    missingEmailsHref,
                    missingFieldsHref,
                    noMissingCount,
                    noMissingHref,
                } = response;

                this.setState({
                    error: null,
                    isLoadingPreview: false,
                    preview: {
                        ...preview,
                        totalCount: totalCount || 0,
                        noMissingCount: noMissingCount || 0,
                        missingEmailsCount: missingEmailsCount || 0,
                        missingFieldsCount: missingFieldsCount || 0,
                        hasReceivedTemplateRecentlyCount: hasReceivedTemplateRecentlyCount || 0,
                        missingFieldsAndReceivedRecentlyCount:
                            missingFieldsAndReceivedRecentlyCount || 0,
                        cannotSendCount: cannotSendCount || 0,
                        noMissingHref: noMissingHref || '#',
                        missingEmailsHref: missingEmailsHref || '#',
                        missingFieldsHref: missingFieldsHref || '#',
                    },
                });
            })
            .fail((response) => {
                // @ts-expect-error
                const parsedResponse = JSON.parse(response.responseText);
                this.setState({
                    error: parsedResponse.errors[0],
                    preview: undefined,
                    isLoadingPreview: false,
                });
            });
    };
}

function getToolTipMessage(
    isMultiSequenceMessage: boolean,
    hasCustomPlaceholders: boolean
): string | null | undefined {
    if (isMultiSequenceMessage) {
        return 'Only single-message templates can be used for bulk email';
    }

    return hasCustomPlaceholders
        ? 'This template contains custom placeholders, which cannot be used with bulk email.'
        : undefined;
}

function convertApiIdsToIntegerIds(apiIds: string[]): string[] {
    // @ts-expect-error
    return apiIds.map((apiId) => {
        return apiId.split('-')[0];
    });
}
